import React, { useState, useEffect, useRef } from 'react';
import { extractErrorMessage } from '../lib/errors';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UploadCloud, FileText, Check, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

export default function Upload() {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState('idle');
  const [contacts, setContacts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('none');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/campaigns').then(r => setCampaigns(r.data)).catch(()=>{});
  }, []);

  const parseFile = async (f) => {
    setFile(f);
    setStep('parsing');
    toast('Parsing your file with AI...');
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post('/imports/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setContacts(r.data.contacts.map(c => ({...c, selected: true})));
      setStep('review');
      if (r.data.contacts.length === 0) toast.error('No contacts could be extracted. Try a different file.');
      else toast.success(`Extracted ${r.data.contacts.length} contacts`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Parsing failed'));
      setStep('idle');
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    // reset so selecting the same file twice re-triggers
    if (e.target) e.target.value = '';
    if (!f) return;
    parseFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) parseFile(f);
  };

  const pickFile = () => inputRef.current && inputRef.current.click();

  const toggle = (id) => setContacts(cs => cs.map(c => c.id===id ? {...c, selected: !c.selected} : c));
  const updateField = (id, field, value) => setContacts(cs => cs.map(c => c.id===id ? {...c, [field]: value} : c));
  const remove = (id) => setContacts(cs => cs.filter(c => c.id !== id));

  const approve = async () => {
    const chosen = contacts.filter(c => c.selected).map(c => ({
      first_name: c.first_name||'', last_name: c.last_name||'', email: c.email||'', phone: c.phone||'', company: c.company||'', job_title: c.job_title||'', status: 'queued'
    }));
    try {
      const r = await api.post('/imports/approve', { contacts: chosen, campaign_id: campaignId === 'none' ? null : campaignId });
      toast.success(`${r.data.created} contacts added`);
      nav('/app/contacts');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to add contacts'));
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="font-display text-3xl">Import contacts</h1>
      <p className="text-zinc-500 mt-1">Upload a CV/resume (PDF or DOCX) or a CSV. AI extracts the contact details and lets you review.</p>

      {step === 'idle' && (
        <div
          className="mt-8 block"
          onDragOver={(e)=>{ e.preventDefault(); setDragOver(true); }}
          onDragEnter={(e)=>{ e.preventDefault(); setDragOver(true); }}
          onDragLeave={()=>setDragOver(false)}
          onDrop={onDrop}
        >
          <Card
            className={`bg-[#111110] border-dashed p-14 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#d4a94a]' : 'border-zinc-800 hover:border-[#d4a94a]'}`}
            onClick={pickFile}
            data-testid="upload-dropzone"
            role="button"
            tabIndex={0}
            onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') { e.preventDefault(); pickFile(); } }}
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-[#1a1815] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a]"><UploadCloud size={26}/></div>
            <div className="mt-5 font-display text-2xl">Drop a file to begin</div>
            <div className="mt-2 text-sm text-zinc-500">PDF, DOCX, or CSV · up to 10MB</div>
            <div className="mt-6">
              <Button
                type="button"
                className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium"
                onClick={(e)=>{ e.stopPropagation(); pickFile(); }}
                data-testid="upload-choose-file-btn"
              >Choose file</Button>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx,.csv,.txt"
              onChange={onFile}
              className="hidden"
              data-testid="upload-file-input"
            />
          </Card>
        </div>
      )}

      {step === 'parsing' && (
        <Card className="mt-8 bg-[#111110] border-zinc-900 p-14 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#1a1815] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a] animate-pulse"><FileText size={26}/></div>
          <div className="mt-5 font-display text-2xl">Extracting contacts…</div>
          <div className="mt-2 text-sm text-zinc-500">{file?.name}</div>
          <div className="mt-6 mx-auto w-64 h-1 rounded-full bg-zinc-900 overflow-hidden">
            <div className="h-full bg-[#d4a94a]" style={{width:'60%'}}/>
          </div>
        </Card>
      )}

      {step === 'review' && (
        <>
          <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="font-display text-2xl">Review extracted contacts</div>
              <div className="text-sm text-zinc-500 mt-1">{contacts.filter(c=>c.selected).length} of {contacts.length} selected</div>
            </div>
            <div className="flex gap-2 items-center">
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger className="w-[210px] bg-[#0a0a0b] border-zinc-800 text-zinc-100"><SelectValue placeholder="Add to campaign"/></SelectTrigger>
                <SelectContent className="bg-[#111110] border-zinc-800 text-zinc-100">
                  <SelectItem value="none">No campaign (just add)</SelectItem>
                  {campaigns.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={()=>{setStep('idle');setFile(null);setContacts([]);}}>Cancel</Button>
              <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={approve}><Check size={16} className="mr-1"/>Approve & queue</Button>
            </div>
          </div>
          <Card className="mt-4 bg-[#111110] border-zinc-900 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 uppercase">
                <tr className="border-b border-zinc-900">
                  <th className="px-4 py-3"></th>
                  <th className="text-left px-4 py-3 font-medium">First</th>
                  <th className="text-left px-4 py-3 font-medium">Last</th>
                  <th className="text-left px-4 py-3 font-medium">Email</th>
                  <th className="text-left px-4 py-3 font-medium">Phone</th>
                  <th className="text-left px-4 py-3 font-medium">Company</th>
                  <th className="text-left px-4 py-3 font-medium">Job title</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-900">
                {contacts.map(c => (
                  <tr key={c.id} className="row-hover">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={c.selected} onChange={()=>toggle(c.id)} className="accent-[#d4a94a]"/>
                    </td>
                    {editingId===c.id ? (
                      ['first_name','last_name','email','phone','company','job_title'].map(f => (
                        <td key={f} className="px-2 py-2"><Input value={c[f]||''} onChange={e=>updateField(c.id, f, e.target.value)} className="h-8 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"/></td>
                      ))
                    ) : (
                      <>
                        <td className="px-4 py-3 text-zinc-100">{c.first_name}</td>
                        <td className="px-4 py-3 text-zinc-100">{c.last_name}</td>
                        <td className="px-4 py-3 text-zinc-300 text-xs">{c.email}</td>
                        <td className="px-4 py-3 text-zinc-400 text-xs">{c.phone || '—'}</td>
                        <td className="px-4 py-3 text-zinc-300">{c.company}</td>
                        <td className="px-4 py-3 text-zinc-400">{c.job_title}</td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={()=>setEditingId(editingId===c.id ? null : c.id)} className="text-zinc-500 hover:text-[#f5d67a]"><Edit2 size={14}/></button>
                        <button onClick={()=>remove(c.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
